import React, { useRef, useEffect, useState } from "react";
import { NavLink, useLocation } from "react-router-dom";
import { useUser } from "src/business-types/grocery/components/UserContext";
import { colorOptions, fontOptions } from "../modules/profile/theme/themeConstants";

const TabIcon = ({ label }: { label: string }) => {
  const getIcon = () => {
    switch (label) {
      case "Print Settings":
        return "🖨️";
      case "Order & Table Settings":
        return "🪑";
      case "Terms & Conditions":
        return "📜";
      case "QR Codes":
        return "📱";
      case "Reports":
        return "📊";
      case "Business Operation":
        return "🕒";
      case "Food Types":
        return "🍔";
      case "Print Config":
        return "🖨️";
      default:
        return "•";
    }
  };

  return <span className="mr-2 text-lg">{getIcon()}</span>;
};

interface TabItem {
  path: string;
  label: string;
}

const ConfigurationNavlink = () => {
  const { user } = useUser();
  const location = useLocation();
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const activeTabRef = useRef<HTMLAnchorElement>(null);
  const [showLeftScroll, setShowLeftScroll] = useState(false);
  const [showRightScroll, setShowRightScroll] = useState(false);

  const bgClass = colorOptions[user?.background_colour || "DodgerBlue"];
  const fontClass = fontOptions[user?.font_family || "Sans-serif"];

  const tabs: TabItem[] = [
    { path: "/v1/confiuration/print-settings", label: "Print Settings" },
    { path: "/v1/confiuration/table-billing", label: "Order & Table Settings" },
    { path: "/v1/confiuration/terms-conditions", label: "Terms & Conditions" },
    { path: "/v1/confiuration/qr-codes", label: "QR Codes" },
    { path: "/v1/confiuration/reports", label: "Reports" },
    ...(user?.business_type === "Restaurant"
      ? [
        { path: "/v1/confiuration/business-operation", label: "Business Operation" },
        { path: "/v1/confiuration/food-types", label: "Food Types" },
      ]
      : []),
    { path: "/v1/confiuration/print-config", label: "Print Config" },
  ];

  // Check scroll position to show/hide buttons
  const checkScrollButtons = () => {
    if (scrollContainerRef.current) {
      const { scrollLeft, scrollWidth, clientWidth } = scrollContainerRef.current;
      setShowLeftScroll(scrollLeft > 10);
      setShowRightScroll(scrollLeft < scrollWidth - clientWidth - 10);
    }
  };

  // Function to scroll active tab to center
  const scrollActiveTabToCenter = () => {
    if (scrollContainerRef.current && activeTabRef.current) {
      const container = scrollContainerRef.current;
      const activeTab = activeTabRef.current;

      const containerWidth = container.offsetWidth;
      const tabWidth = activeTab.offsetWidth;
      const tabLeft = activeTab.offsetLeft;

      const scrollLeft = tabLeft - (containerWidth / 2) + (tabWidth / 2);

      container.scrollTo({
        left: scrollLeft,
        behavior: 'smooth'
      });
    }
  };

  // Scroll functions for buttons
  const scrollLeft = () => {
    if (scrollContainerRef.current) {
      scrollContainerRef.current.scrollBy({
        left: -200,
        behavior: 'smooth'
      });
    }
  };

  const scrollRight = () => {
    if (scrollContainerRef.current) {
      scrollContainerRef.current.scrollBy({
        left: 200,
        behavior: 'smooth'
      });
    }
  };

  // Scroll to center when active tab changes
  useEffect(() => {
    const timeoutId = setTimeout(() => {
      scrollActiveTabToCenter();
    }, 100);

    return () => clearTimeout(timeoutId);
  }, [location.pathname]);

  // Add scroll event listener
  useEffect(() => {
    const container = scrollContainerRef.current;
    if (container) {
      container.addEventListener('scroll', checkScrollButtons);
      checkScrollButtons(); // Initial check

      return () => container.removeEventListener('scroll', checkScrollButtons);
    }
  }, []);

  return (
    <div className="w-full bg-white border-b border-gray-200 shadow-sm relative">
      {/* Full-width container so tabs can use the entire screen */}
      <div className="w-full px-2 sm:px-4 lg:px-6">
        {/* Navigation tabs - Scrollable on mobile, normal on desktop */}
        <div className="border-t border-gray-200 relative">
          {/* Left scroll button - visible on mobile when scrolled */}
          {showLeftScroll && (
            <button
              onClick={scrollLeft}
              className="absolute left-0 top-1/2 -translate-y-1/2 z-10 bg-white/90 rounded-full p-1 shadow-md hover:bg-gray-100 md:hidden"
              aria-label="Scroll left"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
              </svg>
            </button>
          )}

          {/* Right scroll button - visible on mobile when more content to the right */}
          {showRightScroll && (
            <button
              onClick={scrollRight}
              className="absolute right-0 top-1/2 -translate-y-1/2 z-10 bg-white/90 rounded-full p-1 shadow-md hover:bg-gray-100 md:hidden"
              aria-label="Scroll right"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
              </svg>
            </button>
          )}

          <div
            ref={scrollContainerRef}
            className="flex items-center -mb-px gap-0.5 pt-2 overflow-x-auto overflow-y-hidden scrollbar-hide whitespace-nowrap pb-1 scroll-smooth"
          >
            {tabs.map((tab) => {
              const isActive = location.pathname === tab.path;

              return (
                <NavLink
                  key={tab.path}
                  to={tab.path}
                  ref={isActive ? activeTabRef : null}
                  className={`inline-flex items-center px-3 py-2 font-medium text-xs md:text-sm transition-all duration-200 border-b-2 flex-shrink-0 ${fontClass} ${isActive ? `${bgClass} text-white border-transparent rounded-t-lg` : "text-gray-500 hover:text-gray-700 border-transparent hover:border-gray-300"
                    }`}
                >
                  <TabIcon label={tab.label} />
                  <span>{tab.label}</span>
                </NavLink>
              );
            })}
          </div>
        </div>
      </div>
    </div>
  );
};

export default ConfigurationNavlink;